import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { ifDefined } from 'lit-html/directives/if-defined';

import connect from '@elab/core/store/connect';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import slotsUtils from '@elab/core/utils/ui/slotsUtils';
import { cacheSelector, compareState } from '@elab/core/utils/webComponent';
import { actions as registrationAction } from '@elab/components/connected/forms/RegistrationForm/redux/index';

import { FormElement } from './Form';
/**
 * Component is used for password field functionality
 *
 *
 * HTML Structure Example
 *
 * <e2-password-field name="Een veilig wachtwoord bestaat uit">
 * </e2-password-field>
 */
export class PasswordField extends FormElement {
    static get properties() {
        return {
            type: { type: String, attribute: false },
            errorAlwaysFocused: { type: Boolean },
            label: { type: String },
            caption: { type: String },
            placeholder: { type: String },
            value: { type: String, attribute: false },
            untouched: { type: Boolean, attribute: false },
            errorFocused: { type: Boolean, attribute: false },
            disabled: { type: Boolean, reflect: true },
            error: { type: Boolean, attribute: false },
            required: { type: Boolean, reflect: true },
            modifiers: { type: String },
            noMaxlength: { type: Boolean, attribute: 'no-maxlength' },
            isLabelReverse: { type: Boolean, attribute: 'is-label-reverse' },
            gtmLabel: {
                type: String,
                attribute: 'gtm-label',
            },
            withIcon: { type: Boolean, attribute: 'with-icon' },
            asmSession: { type: Boolean, attribute: 'asm-session' },
        };
    }

    constructor() {
        super();
        this.type = 'password';
        this.untouched = true;
        this.errorAlwaysFocused = false;
        this.errorFocused = false;
        this.value = '';
        this.error = false;
        this.noMaxlength = false;
        this.disabled = false;
        this.isLabelReverse = false;
        this.gtmLabel = '';
        this.withIcon = false;
        this.asmSession = false;
        this.setAsmPassword = false;
    }

    shouldUpdate(changedProps) {
        return [
            ['type'],
            ['errorAlwaysFocused'],
            ['label'],
            ['value'],
            ['untouched'],
            ['errorFocused'],
            ['error'],
        ].some(item => compareState(this, changedProps, ...item));
    }

    toggleFieldType() {
        this.type = this.type === 'password' ? 'text' : 'password';
    }

    updated(changedProps) {
        if (changedProps.has('fieldValue') && this.fieldValue) {
            this.required = this.isRequired();
            if (this.doSetFakePassword()) {
                this.setFakePassword();
            }
        }
    }

    generateNewPassword() {
        const chars = '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const passwordLength = 8;
        let password = '';

        for (let i = 0; i <= passwordLength; i++) {
            const randomNumber = Math.floor(Math.random() * chars.length);
            password += chars.substring(randomNumber, randomNumber + 1);
        }

        return password;
    }

    doSetFakePassword() {
        return this.asmSession && !this.value && this.storeName === 'registrationForm';
    }

    setFakePassword() {
        const password = this.generateNewPassword();
        setTimeout(() => {
            this.dispatchEvent(new CustomEvent('remove-password-validation'));
            this.dispatchEvent(
                new CustomEvent('INITIALIZE-DATA-FIELD-VALUE', {
                    detail: password,
                }),
            );
        }, 500);
    }

    connectedCallback() {
        super.connectedCallback();
        if (this.doSetFakePassword()) {
            this.setFakePassword();
        }
    }

    isRequired() {
        return !!this.required;
    }

    firstUpdated() {
        slotsUtils.emulateSlots(this);
    }

    handlePaste(e) {
        e.preventDefault();
        return false;
    }

    render() {
        return html`
            <div class="password-field">
                <e2-input-field
                    class="${this.getInputFieldClasses()}"
                    name="${this.name}"
                    store-name="${this.storeName}"
                    placeholder="${this.placeholder}"
                    modifiers="password ${this.modifiers}"
                    @paste=${this.handlePaste}
                    type="${this.type}"
                    ?disabled="${this.disabled}"
                    ?required="${this.required}"
                    .noMaxlength="${this.noMaxlength}"
                    .isLabelReverse="${this.isLabelReverse}"
                    gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                >
                    ${this.renderLabelSlot()}
                    <span v-slot="caption">${this.caption}</span>
                    <span v-slot="icon">
                        <i class="${this.getIconClasses()}" @click=${this.toggleFieldType}> </i>
                    </span>
                    <span v-slot="extra-label">
                        <v-slot name="extra-lbl" class="emulate-slot"></v-slot>
                    </span>
                </e2-input-field>
                <span v-slot="icon"></span>
                <div class="${this.getErrorClasses()}">
                    <span class="password-error-icon">
                        <v-slot name="password-error-icon"></v-slot>
                    </span>
                    <v-slot
                        class="${this.errorFocused || this.value ? 'd-contents' : 'd-none'}"
                        name="psw-error"
                    ></v-slot>
                    <v-slot
                        class="${this.errorFocused || this.value ? 'd-none' : 'd-contents'}"
                        name="underline-error"
                    ></v-slot>
                </div>
            </div>
        `;
    }

    renderLabelSlot() {
        return html`
            <span v-slot="label" class="label-slot">
                ${this.label}
                <v-slot name="required_label" class="emulate-slot"></v-slot>
                <v-slot name="optional_label" class="emulate-slot"></v-slot>
            </span>
        `;
    }

    getInputFieldClasses() {
        return classMap({
            'password-field': true,
            'password-field--untouched': this.untouched && !this.errorAlwaysFocused,
        });
    }

    getErrorClasses() {
        return classMap({
            'password-error': true,
            'password-error--default': this.errorFocused || this.value,
            'password-error--underline': !this.errorFocused && !this.value,
            'password-error--focused': this.errorFocused,
            'password-error--visible': this.error,
            'password-error--none': !this.errorFocused && !this.error,
        });
    }

    getIconClasses() {
        return classMap({
            'password-unhide': true,
            'password-unhide--position-adjust': true,
            icon: this.withIcon,
            'icon-eye2': this.withIcon && this.type === 'password',
            'icon-eye-slash': this.withIcon && this.type !== 'password',
            'password-unhide--icon-appears': this.value,
            'password-unhide--active': this.type !== 'password',
        });
    }
}

const mapDispatchToProps = dispatch => ({
    'remove-password-validation': () => {
        dispatch(registrationAction.removePasswordValidation());
    },
});

const mapStateToProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);
    const focused = selectors.isDataFieldFocused(state, ctx.name);
    const value = selectors.getDataFieldValue(state, ctx.name);

    return {
        value,
        error: selectors.getDataFieldError(state, ctx.name),
        untouched: (!focused && ctx.untouched) || (!value && !focused),
        errorFocused: focused || ctx.errorAlwaysFocused,
        required: selectors.isDataFieldRequired(state, ctx.name),
    };
};

export default () =>
    customElements.define(
        'e2-password-field',
        connect(mapStateToProps, mapDispatchToProps)(PasswordField),
    );
